import { Skeleton } from "@credit-acceptance/uikit";
import { FileText, Mail, MailOpen, MessageSquare } from "lucide-react";
import type { KeyboardEvent } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { getErrorMessage } from "../../lib/query/errors";
import { cn } from "../../lib/utils";
import { useReadOnly } from "../lock/ReadOnlyContext";
import type { DocumentOrMessage } from "./api/types";
import { useDocumentsAndMessages } from "./api/useDocumentsAndMessages";
import { mapDocumentsAndMessages, matchesFilter } from "./documentsAndMessages.mapping";
import { formatReceived } from "./formatReceived";
import { openDocumentViewerWindow } from "./openViewerWindow";
import SendProofsModal from "./SendProofsModal";

/** Default filter used before the deal's actual default (`meta.defaultFilter`) has loaded. */
const FALLBACK_FILTER = "SHOW_ALL";

interface DocumentsAndMessagesPanelProperties {
  approvalId: string;
  /** Dealer id from the deal summary, forwarded to proof uploads. */
  dealerId?: string;
}

function DocumentsAndMessagesPanel({ approvalId, dealerId }: DocumentsAndMessagesPanelProperties) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const { data, isLoading, isError, error } = useDocumentsAndMessages(approvalId);
  const view = data ? mapDocumentsAndMessages(data) : undefined;
  const [sendProofsOpen, setSendProofsOpen] = useState(false);

  // Filter selection. `undefined` until the analyst picks a filter explicitly, in which case the
  // deal's own `meta.defaultFilter` applies — this is what lets the default vary per deal
  // (eContracting/contract-method) without a round trip once the data has loaded.
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>();
  // ReviewPage doesn't remount this panel on an approvalId route-param change, so an explicit
  // filter pick on one deal would otherwise leak into the next deal's view.
  useEffect(() => {
    setSelectedFilter(undefined);
  }, [approvalId]);
  const supportedFilters = data?.meta.supportedFilters ?? [];
  // Only apply a filter the BFF actually advertised for this deal — if supportedFilters comes
  // back empty (fully degraded filter context), fall back to showing everything rather than
  // silently filtering by a defaultFilter with no visible control to change it.
  const activeFilter =
    supportedFilters.length === 0 ? FALLBACK_FILTER : (selectedFilter ?? data?.meta.defaultFilter ?? FALLBACK_FILTER);
  const filteredItems = view?.items.filter((item) => matchesFilter(activeFilter, item)) ?? [];

  // Open a viewable document in its own dedicated viewer window (OR-22558: one
  // window per document). Identifiers travel in the URL; display metadata
  // travels via a `sessionStorage` handoff written inside
  // `openDocumentViewerWindow` — no shared store or handshake needed.
  const openDocument = (item: DocumentOrMessage) => {
    if (item.kind !== "doc" || !item.viewable || !item.docId) {
      return;
    }
    openDocumentViewerWindow({
      appId: approvalId,
      docId: item.docId,
      fileName: item.fileName ?? item.name,
      reviewedBy: item.reviewedBy,
    });
  };

  return (
    <section className="border-border bg-card flex flex-col overflow-hidden rounded-lg border">
      <header className="border-border bg-section-header sticky top-0 flex items-center justify-between gap-2 border-b px-4 py-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="text-foreground text-sm font-semibold">{t("documents.heading")}</h2>
          {view && view.unreadMessageCount > 0 && (
            <Badge variant="destructive" aria-label={t("documents.unreadBadge", { count: view.unreadMessageCount })}>
              {view.unreadMessageCount}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {supportedFilters.length > 0 && (
            <Select value={activeFilter} onValueChange={setSelectedFilter}>
              <SelectTrigger
                id="documents-filter"
                aria-label={t("documents.filter.label")}
                className="h-7 w-36 text-xs"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {supportedFilters.map((filterOption) => (
                  <SelectItem key={filterOption} value={filterOption}>
                    {t(`documents.filter.${filterOption}`, { defaultValue: filterOption })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button size="xs" onClick={() => setSendProofsOpen(true)} disabled={readOnly}>
            {t("documents.sendProofs")}
          </Button>
        </div>
      </header>

      <SendProofsModal open={sendProofsOpen} onOpenChange={setSendProofsOpen} appId={approvalId} dealerId={dealerId} />

      {isLoading && (
        <div role="status" aria-label={t("documents.loading")} className="p-4">
          <Skeleton loading active paragraph={{ rows: 3 }} />
        </div>
      )}

      {isError && (
        <p role="alert" className="text-destructive px-4 py-3 text-xs">
          {t("documents.loadError", { message: getErrorMessage(error) })}
        </p>
      )}

      {view && filteredItems.length === 0 && (
        <p className="text-muted-foreground px-4 py-6 text-center text-xs">{t("documents.empty")}</p>
      )}

      {view && filteredItems.length > 0 && (
        <ul
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- intentional: makes this scrollable region keyboard-scrollable (WCAG 2.1.1); the list itself stays non-interactive.
          tabIndex={0}
          aria-label={t("documents.listLabel")}
          className="divide-border max-h-[225px] min-h-[100px] divide-y overflow-y-auto"
        >
          {filteredItems.map((item) => {
            const isUnreviewedDocument = item.kind === "doc" && !item.reviewedBy;
            const isUnread = item.unread === true || isUnreviewedDocument;
            // Assumes all "Processing" status documents are FAX proofs (currently the only optimistic upload type).
            const isProcessing = item.kind === "doc" && item.name === "FAX" && item.receivedAt === "Processing";
            const isOpenable = !isProcessing && item.kind === "doc" && item.viewable === true && Boolean(item.docId);
            const icon =
              item.kind === "doc" ? <FileText className="size-3.5" /> : <MessageSquare className="size-3.5" />;
            // Envelope indicator for documents only: closed (light yellow) when
            // unreviewed, opened (default outline) once a reviewer is set.
            const envelopeIcon =
              item.kind === "doc" ? (
                item.reviewedBy ? (
                  <MailOpen className="text-muted-foreground size-3.5 shrink-0" aria-hidden="true" />
                ) : (
                  <Mail
                    className="text-muted-foreground size-4 shrink-0"
                    fill="currentColor"
                    stroke="var(--color-card)"
                    aria-hidden="true"
                  />
                )
              ) : null;
            const nameClasses = cn("min-w-0 flex-1 truncate text-2xs", isUnread ? "font-semibold" : "font-normal");
            const rowInteractionProperties = isOpenable
              ? {
                  role: "button" as const,
                  tabIndex: 0,
                  "aria-label": t("documents.openDocument", { name: item.name }),
                  onClick: () => openDocument(item),
                  onKeyDown: (event: KeyboardEvent<HTMLLIElement>) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openDocument(item);
                    }
                  },
                }
              : {};
            return (
              <li
                key={item.id}
                className={cn(
                  "hover:bg-muted grid grid-cols-12 items-center gap-2 py-1 pl-2 pr-4 text-xs transition-colors",
                  isOpenable && "cursor-pointer"
                )}
                {...rowInteractionProperties}
              >
                <span className="text-foreground col-span-6 flex min-w-0 items-center gap-1.5">
                  {envelopeIcon}
                  <span aria-hidden="true" className="text-muted-foreground shrink-0">
                    {icon}
                  </span>
                  <span className={nameClasses}>
                    {item.name}
                    {item.pageCount ? ` (${item.pageCount})` : ""}
                  </span>
                </span>
                <span
                  className={cn(
                    "text-muted-foreground text-2xs col-span-4 truncate",
                    isUnread ? "font-semibold" : "font-normal"
                  )}
                >
                  {formatReceived(item.receivedAt)}
                </span>
                <span
                  className={cn(
                    "text-muted-foreground text-2xs col-span-2 truncate uppercase tracking-wide",
                    isUnread ? "font-semibold" : "font-normal"
                  )}
                >
                  {item.kind === "doc" ? (item.reviewedBy ?? "") : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default DocumentsAndMessagesPanel;
